import { apiRequestWithAutoRefresh } from '../utils/apiUtils'

// 將 Base64 URL 編碼的字符串轉換為 Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export interface PushSubscription {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
}

// 記錄「哪位使用者在這台裝置啟用過推播」（值為 userId），用來在訂閱被瀏覽器清掉後自動補訂
const PUSH_ENABLED_KEY = 'push-notifications-enabled'
// App 開啟／回到前景時同步訂閱到後端的最短間隔
const SYNC_INTERVAL_MS = 60 * 60 * 1000

function isSameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a) return false
  const bytes = new Uint8Array(a)
  return bytes.length === b.length && bytes.every((v, i) => v === b[i])
}

export class PushNotificationService {
  private static vapidPublicKey: string | null = null
  private static lastSyncAt = 0
  private static lastSyncUserId: string | null = null
  private static syncing: Promise<void> | null = null

  // 檢查 PWA 是否可安裝（基於基本要求）
  static isPWAInstallable(): boolean {
    return (
      'serviceWorker' in navigator &&
      window.matchMedia('(display-mode: browser)').matches
    )
  }

  // 檢查 PWA 是否已安裝
  static isPWAInstalled(): boolean {
    // 檢查是否在 standalone 模式運行（已安裝為 PWA）
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    // iOS Safari 特殊檢測
    const isInApp = (window.navigator as any).standalone === true
    
    return isStandalone || isInApp
  }

  // 檢測瀏覽器類型和平台
  static getBrowserInfo(): { isIOS: boolean; isSafari: boolean; isAndroid: boolean; isChrome: boolean } {
    const ua = navigator.userAgent
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua)
    const isAndroid = /android/i.test(ua)
    const isChrome = /chrome|chromium|crios/i.test(ua) && !/edg/i.test(ua)
    
    return { isIOS, isSafari, isAndroid, isChrome }
  }

  // 檢查推送通知是否被支援
  static isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    )
  }

  // 檢查通知權限狀態
  static getPermissionStatus(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied'
    }
    return Notification.permission
  }

  // 請求通知權限
  static async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported')
    }

    const permission = await Notification.requestPermission()
    console.log('Notification permission:', permission)
    return permission
  }

  // 獲取 VAPID 公鑰
  // 以後端為準（推播是後端用私鑰簽的），前端 build 時的環境變數只當備援，
  // 避免 build 參數跟後端金鑰不一致時，訂閱建在錯的金鑰上、推播永遠 403。
  static async getVapidPublicKey(): Promise<string> {
    return (await this.resolveVapidKey()).key
  }

  // fromServer=false 代表拿到的是 env 備援金鑰：不快取（下次再問後端），
  // 也不拿它來判定既有訂閱「金鑰不符」，免得後端暫時連不上時把正常的訂閱退掉。
  private static async resolveVapidKey(): Promise<{ key: string; fromServer: boolean }> {
    if (this.vapidPublicKey) {
      return { key: this.vapidPublicKey, fromServer: true }
    }

    try {
      const response = await apiRequestWithAutoRefresh('/push/vapid-public-key', { method: 'GET' })
      // 注意：response 直接包含 publicKey，不是 response.data.publicKey
      const publicKey = (response as any).publicKey as string | undefined
      if (publicKey) {
        this.vapidPublicKey = publicKey
        return { key: publicKey, fromServer: true }
      }
    } catch (error) {
      console.error('Failed to get VAPID public key:', error)
    }

    const envKey = import.meta.env.VITE_VAPID_PUBLIC_KEY
    if (envKey) {
      return { key: envKey, fromServer: false }
    }

    throw new Error('無法獲取 VAPID 公鑰，請確認後端服務正常運行')
  }

  // 取得與目前 VAPID 金鑰相符的訂閱；金鑰不符（後端換過金鑰）或 forceRenew 時退訂重建
  private static async ensureBrowserSubscription(
    registration: ServiceWorkerRegistration,
    { createIfMissing, forceRenew = false }: { createIfMissing: boolean; forceRenew?: boolean }
  ): Promise<globalThis.PushSubscription | null> {
    const { key, fromServer } = await this.resolveVapidKey()
    const vapidKey = urlBase64ToUint8Array(key)
    let subscription = await registration.pushManager.getSubscription()

    const keyMismatch = !!subscription && fromServer &&
      !isSameKey(subscription.options.applicationServerKey, vapidKey)

    if (subscription && (keyMismatch || forceRenew)) {
      console.log(keyMismatch
        ? 'ℹ️ 既有推送訂閱的 VAPID 金鑰與後端不符，重新訂閱'
        : 'ℹ️ 既有推送訂閱已被推播服務作廢，重新訂閱')
      await subscription.unsubscribe()
      subscription = null
      createIfMissing = true
    }

    if (!subscription && createIfMissing) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey,
      })
    }

    return subscription
  }

  // 把瀏覽器訂閱送到後端（後端 upsert，並把 isActive 設回 true）。
  // reactivated=true 代表後端原本已停用這筆（推播服務回過 410），瀏覽器手上的訂閱其實是死的。
  private static async sendSubscriptionToServer(
    subscription: globalThis.PushSubscription
  ): Promise<{ data: PushSubscription; reactivated: boolean }> {
    const p256dhKey = subscription.getKey('p256dh')
    const authKey = subscription.getKey('auth')

    if (!p256dhKey || !authKey) {
      throw new Error('無法獲取訂閱密鑰')
    }

    const subscriptionData: PushSubscription = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64(p256dhKey),
        auth: arrayBufferToBase64(authKey),
      },
    }

    const response = await apiRequestWithAutoRefresh('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription: subscriptionData })
    })

    return { data: subscriptionData, reactivated: (response as any)?.reactivated === true }
  }

  // App 開啟／回到前景時呼叫：把這台裝置的訂閱重新同步到後端。
  // 修正「過一段時間通知自動停用」：瀏覽器輪換或清掉訂閱、或後端收到 410 把訂閱停用後，
  // 原本沒有任何流程會補回來。這裡在權限仍是 granted、且是「同一位使用者」啟用過時：
  // - 瀏覽器還有訂閱 → 重新 upsert 到後端；若後端說這筆早已被 410 作廢，就換一個新訂閱
  // - 瀏覽器訂閱不見了 → 靜默重新訂閱
  // 換別人登入同一台裝置不會動到訂閱（避免 B 沒同意就收到通知、或 A 的裝置收到 B 的通知）。
  // 失敗一律吞掉，不打擾使用者（例如 iOS 可能要求使用者手勢才能訂閱）。
  static async syncSubscription(
    userId: string,
    { force = false }: { force?: boolean } = {}
  ): Promise<void> {
    if (!userId || !this.isSupported() || Notification.permission !== 'granted') return
    if (this.lastSyncUserId !== userId) {
      this.lastSyncAt = 0
    }
    if (!force && Date.now() - this.lastSyncAt < SYNC_INTERVAL_MS) return
    if (this.syncing) return this.syncing

    this.syncing = (async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration()
        if (!registration?.active) return

        const enabledFor = localStorage.getItem(PUSH_ENABLED_KEY)
        const existing = await registration.pushManager.getSubscription()

        if (enabledFor && enabledFor !== userId) return
        // 舊版沒有記錄旗標：瀏覽器有訂閱就視為目前使用者啟用過（一次性遷移）
        if (!enabledFor && !existing) return

        const subscription = await this.ensureBrowserSubscription(registration, { createIfMissing: true })
        if (!subscription) return

        const { reactivated } = await this.sendSubscriptionToServer(subscription)
        if (reactivated) {
          const renewed = await this.ensureBrowserSubscription(registration, { createIfMissing: true, forceRenew: true })
          if (renewed) await this.sendSubscriptionToServer(renewed)
        }

        localStorage.setItem(PUSH_ENABLED_KEY, userId)
        this.lastSyncAt = Date.now()
        this.lastSyncUserId = userId
      } catch (error) {
        console.warn('同步推送訂閱失敗（下次開啟時再試）:', error)
      } finally {
        this.syncing = null
      }
    })()

    return this.syncing
  }

  // 訂閱推送通知
  static async subscribe(userId: string): Promise<PushSubscription | null> {
    try {
      console.log('🚀 開始訂閱推送通知...')
      
      // 1. 檢查支援性
      if (!this.isSupported()) {
        console.error('❌ 瀏覽器不支援推送通知')
        throw new Error('您的瀏覽器不支援推送通知功能，請使用 Chrome、Firefox 或 Edge 瀏覽器')
      }
      console.log('✅ 瀏覽器支援推送通知')

      // 2. 檢查 Service Worker 註冊狀態
      const swRegistration = await navigator.serviceWorker.getRegistration()
      if (!swRegistration) {
        console.error('❌ Service Worker 未註冊')
        throw new Error('Service Worker 未就緒，請重新整理頁面後再試')
      }
      console.log('✅ Service Worker 已註冊:', swRegistration.scope)

      // 3. 檢查通知權限
      const permission = this.getPermissionStatus()
      console.log('📋 當前通知權限:', permission)
      
      if (permission === 'denied') {
        throw new Error('通知權限已被拒絕，請在瀏覽器設定中允許通知權限後重試')
      }

      if (permission === 'default') {
        console.log('🔔 請求通知權限...')
        const newPermission = await this.requestPermission()
        console.log('📋 新的通知權限:', newPermission)
        
        if (newPermission !== 'granted') {
          throw new Error('需要允許通知權限才能啟用推送通知')
        }
      }
      console.log('✅ 通知權限已授予')

      // 4. 等待 Service Worker 就緒
      console.log('⏳ 等待 Service Worker 就緒...')
      const registration = await navigator.serviceWorker.ready
      console.log('✅ Service Worker 已就緒')

      // 5. 取得（或建立）與目前 VAPID 金鑰相符的訂閱
      console.log('📝 確認推送訂閱...')
      const subscription = await this.ensureBrowserSubscription(registration, { createIfMissing: true })
      if (!subscription) {
        throw new Error('無法建立推送訂閱')
      }
      console.log('✅ 推送訂閱已就緒')

      // 6. 將訂閱發送到服務器
      console.log('📤 將訂閱發送到服務器...')
      const { data: subscriptionData } = await this.sendSubscriptionToServer(subscription)
      localStorage.setItem(PUSH_ENABLED_KEY, userId)
      this.lastSyncAt = Date.now()
      this.lastSyncUserId = userId
      console.log('✅ 訂閱已成功發送到服務器')
      console.log('🎉 推送通知訂閱完成！')

      return subscriptionData
    } catch (error) {
      console.error('❌ 訂閱推送通知失敗:', error)
      
      // 提供更友好的錯誤訊息
      if (error instanceof Error) {
        if (error.message.includes('permission')) {
          throw new Error('通知權限問題：' + error.message)
        } else if (error.message.includes('Service Worker')) {
          throw new Error('Service Worker 問題：' + error.message)
        } else if (error.message.includes('not supported')) {
          throw new Error('瀏覽器支援問題：' + error.message)
        } else {
          throw error
        }
      }
      throw new Error('訂閱失敗，請稍後再試')
    }
  }

  // 取消訂閱推送通知
  static async unsubscribe(): Promise<void> {
    try {
      if (!this.isSupported()) {
        return
      }

      // 使用者主動關閉：之後不要自動補訂
      localStorage.removeItem(PUSH_ENABLED_KEY)

      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        // 從服務器移除訂閱
        await apiRequestWithAutoRefresh('/push/unsubscribe', {
          method: 'POST',
          body: JSON.stringify({ endpoint: subscription.endpoint })
        })

        // 取消瀏覽器訂閱
        await subscription.unsubscribe()
        console.log('Unsubscribed from push notifications')
      }
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error)
      throw error
    }
  }

  // 檢查是否已訂閱
  static async isSubscribed(): Promise<boolean> {
    try {
      if (!this.isSupported()) {
        return false
      }

      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      return subscription !== null
    } catch (error) {
      console.error('Failed to check subscription status:', error)
      return false
    }
  }

  // 獲取訂閱列表
  static async getSubscriptions() {
    try {
      const response = await apiRequestWithAutoRefresh('/push/subscriptions', { method: 'GET' })
      return response.data.subscriptions
    } catch (error) {
      console.error('Failed to get subscriptions:', error)
      return []
    }
  }

  // 發送測試通知
  static async sendTestNotification(): Promise<void> {
    try {
      await apiRequestWithAutoRefresh('/push/test', { method: 'POST' })
      console.log('Test notification sent')
    } catch (error) {
      console.error('Failed to send test notification:', error)
      throw error
    }
  }
}

// 輔助函數：將 ArrayBuffer 轉換為 Base64
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

export default PushNotificationService
